import { action, makeObservable } from 'mobx';
import { Input } from '../../components/input/input';
import { InputState, InputStateTypes } from '../../components/input/input.state';
import { IkeInputStateGroup } from '../../components/input/input.state.group';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Logger } from '../../services';
import { ForgotPasswordCtrl } from './forgot.password.ctrl';

const CSS = {
	container: 'c-ForgotPassword',
	heading: 'c-ForgotPassword__Heading',
	acceptButton: 'c-ForgotPassword__Accept',
};

addStyles({
	[CSS.container]: {
		display: 'flex',
		'flex-direction': 'column',
		'align-items': 'center',
	},
	[CSS.acceptButton]: {
		width: '300px',
	},
});

export class ForgotPasswordComponent extends IMithrilStateComponent {
	ctrl: ForgotPasswordCtrl;

	email: InputState = new InputState(T.PASSWORD_FORGOT_EMAIL);

	form = new IkeInputStateGroup();

	constructor(ctrl: ForgotPasswordCtrl) {
		super();

		makeObservable(this);

		this.ctrl = ctrl;

		this.form.add(this.email);

		this.email.isRequired = true;
		this.email.type = InputStateTypes.TEXT;
	}

	override onCreate() {
		this.autorun((_) => this.validate());
	}

	get nodes(): MithrilVNode {
		return m('form', { className: CSS.container }, [
			m('h3', Translate(T.PASSWORD_FORGOT_TITLE)),
			this.ctrl.completedReset ? null : Input.view(this.email),
			this.ctrl.completedReset ? this.viewNextSteps() : this.viewAcceptButton(),
		]);
	}

	viewAcceptButton(): MithrilVNode {
		const child = this.ctrl.loading ? Spinner.view() : Translate(T.PASSWORD_FORGOT_ACCEPT);
		const disabled = this.ctrl.loading || !this.form.canSubmit;

		return m(
			'button',
			{
				className: `${CSS.acceptButton} mdl-button mdl-button--raised mdl-button--accent`,
				type: 'submit',
				onclick: this.submit,
				disabled,
			},
			child
		);
	}

	viewNextSteps(): MithrilVNode {
		return m('p', Translate(T.PASSWORD_FORGOT_SUCCESS));
	}

	@action.bound submit() {
		if (!this.form.canSubmit) {
			Logger.error('Attempted to submit invalid form...');
			return false;
		}

		if (this.email.value) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.ctrl.requestPasswordReset(this.email.value);
		}

		// Don't submit form
		return false;
	}

	validate() {
		if (!this.ctrl.isValidEmail(this.email.value)) {
			this.email.setError(T.EMAIL_INVALID);
		} else {
			this.email.setError('');
		}
	}
}
